import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'

import * as jobsApi from '@/services/api/endpoints/jobs'
import { BackendJob, JobListParams, RecommendedJob } from '@/services/api/endpoints/jobs'
import * as applicationsApi from '@/services/api/endpoints/applications'
import { BackendApplication } from '@/services/api/endpoints/applications'
import { ApiError } from '@/services/api/types'

/**
 * Backend-driven jobs/applications state for the STUDENT experience only.
 * Kept separate from the legacy Firestore-backed jobsSlice (used by the
 * still-unmigrated employer job-posting/hiring-pipeline screens) so this
 * migration doesn't have to touch that out-of-scope code path at all.
 */
interface StudentJobsState {
  applyingJobIds: string[]
  jobs: BackendJob[]
  jobsLoading: boolean
  jobsPage: number
  jobsPages: number
  myApplications: BackendApplication[]
  myApplicationsLoading: boolean
  recommendedJobs: RecommendedJob[]
  recommendedJobsLoading: boolean
  savedJobIds: string[]
  savedJobsLoading: boolean
  selectedApplication: BackendApplication | null
  selectedApplicationLoading: boolean
  selectedJob: BackendJob | null
  selectedJobLoading: boolean
}

const initialState: StudentJobsState = {
  applyingJobIds: [],
  jobs: [],
  jobsLoading: false,
  jobsPage: 1,
  jobsPages: 1,
  myApplications: [],
  myApplicationsLoading: false,
  recommendedJobs: [],
  recommendedJobsLoading: false,
  savedJobIds: [],
  savedJobsLoading: false,
  selectedApplication: null,
  selectedApplicationLoading: false,
  selectedJob: null,
  selectedJobLoading: false,
}

const errorMessage = (error: unknown, fallback: string): string => (error instanceof ApiError ? error.message : fallback)

export const fetchJobs = createAsyncThunk('studentJobs/fetchJobs', async ({ append = false, params }: { append?: boolean; params?: JobListParams } = {}, { rejectWithValue }) => {
  try {
    const { data, metadata } = await jobsApi.listJobs(params)
    return { append, data, page: metadata?.page ?? 1, pages: metadata?.pages ?? 1 }
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load jobs'))
  }
})

export const fetchJob = createAsyncThunk('studentJobs/fetchJob', async (id: string, { rejectWithValue }) => {
  try {
    return await jobsApi.getJob(id)
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load job'))
  }
})

export const fetchSavedJobs = createAsyncThunk('studentJobs/fetchSavedJobs', async (_, { rejectWithValue }) => {
  try {
    return await jobsApi.listSavedJobs()
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load saved jobs'))
  }
})

export const fetchRecommendedJobs = createAsyncThunk('studentJobs/fetchRecommendedJobs', async (_, { rejectWithValue }) => {
  try {
    return await jobsApi.listRecommendedJobs()
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load recommendations'))
  }
})

export const toggleSaveJob = createAsyncThunk(
  'studentJobs/toggleSaveJob',
  async (jobId: string, { rejectWithValue }) => {
    try {
      const { saved } = await jobsApi.toggleSaveJob(jobId)
      return { jobId, saved }
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to update saved job'))
      return rejectWithValue(errorMessage(error, 'Failed to update saved job'))
    }
  },
)

export const applyToJob = createAsyncThunk(
  'studentJobs/applyToJob',
  async (jobId: string, { rejectWithValue }) => {
    try {
      const application = await applicationsApi.apply(jobId)
      toast.success('Application submitted')
      return application
    } catch (error) {
      const message = errorMessage(error, 'Failed to apply to job')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const fetchMyApplications = createAsyncThunk('studentJobs/fetchMyApplications', async (_, { rejectWithValue }) => {
  try {
    const { data } = await applicationsApi.listMyApplications()
    return data
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load applications'))
  }
})

export const withdrawApplication = createAsyncThunk(
  'studentJobs/withdrawApplication',
  async (applicationId: string, { rejectWithValue }) => {
    try {
      const application = await applicationsApi.withdrawApplication(applicationId)
      toast.success('Application withdrawn')
      return application
    } catch (error) {
      const message = errorMessage(error, 'Failed to withdraw application')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const fetchApplicationDetail = createAsyncThunk('studentJobs/fetchApplicationDetail', async (id: string, { rejectWithValue }) => {
  try {
    return await applicationsApi.getApplication(id)
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load application'))
  }
})

export const acceptOffer = createAsyncThunk('studentJobs/acceptOffer', async (offerId: string, { rejectWithValue }) => {
  try {
    await applicationsApi.acceptOffer(offerId)
    toast.success('Offer accepted')
  } catch (error) {
    const message = errorMessage(error, 'Failed to accept offer')
    toast.error(message)
    return rejectWithValue(message)
  }
})

export const declineOffer = createAsyncThunk('studentJobs/declineOffer', async (offerId: string, { rejectWithValue }) => {
  try {
    await applicationsApi.declineOffer(offerId)
    toast.success('Offer declined')
  } catch (error) {
    const message = errorMessage(error, 'Failed to decline offer')
    toast.error(message)
    return rejectWithValue(message)
  }
})

const studentJobsSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobs.pending, (state) => {
        state.jobsLoading = true
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.jobs = action.payload.append ? [...state.jobs, ...action.payload.data] : action.payload.data
        state.jobsPage = action.payload.page
        state.jobsPages = action.payload.pages
        state.jobsLoading = false
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.jobsLoading = false
        toast.error(action.payload as string)
      })
      .addCase(fetchJob.pending, (state) => {
        state.selectedJobLoading = true
      })
      .addCase(fetchJob.fulfilled, (state, action) => {
        state.selectedJob = action.payload
        state.selectedJobLoading = false
      })
      .addCase(fetchJob.rejected, (state, action) => {
        state.selectedJobLoading = false
        state.selectedJob = null
        toast.error(action.payload as string)
      })
      .addCase(fetchSavedJobs.pending, (state) => {
        state.savedJobsLoading = true
      })
      .addCase(fetchSavedJobs.fulfilled, (state, action) => {
        state.savedJobIds = action.payload.map((job) => job.id)
        state.savedJobsLoading = false
      })
      .addCase(fetchSavedJobs.rejected, (state, action) => {
        state.savedJobsLoading = false
        toast.error(action.payload as string)
      })
      .addCase(fetchRecommendedJobs.pending, (state) => {
        state.recommendedJobsLoading = true
      })
      .addCase(fetchRecommendedJobs.fulfilled, (state, action) => {
        state.recommendedJobs = action.payload
        state.recommendedJobsLoading = false
      })
      .addCase(fetchRecommendedJobs.rejected, (state, action) => {
        state.recommendedJobsLoading = false
        toast.error(action.payload as string)
      })
      .addCase(toggleSaveJob.fulfilled, (state, action) => {
        const { jobId, saved } = action.payload
        state.savedJobIds = saved ? [...state.savedJobIds, jobId] : state.savedJobIds.filter((id) => id !== jobId)
      })
      .addCase(applyToJob.pending, (state, action) => {
        state.applyingJobIds.push(action.meta.arg)
      })
      .addCase(applyToJob.fulfilled, (state, action) => {
        state.applyingJobIds = state.applyingJobIds.filter((id) => id !== action.meta.arg)
        state.myApplications.push(action.payload)
      })
      .addCase(applyToJob.rejected, (state, action) => {
        state.applyingJobIds = state.applyingJobIds.filter((id) => id !== action.meta.arg)
      })
      .addCase(fetchMyApplications.pending, (state) => {
        state.myApplicationsLoading = true
      })
      .addCase(fetchMyApplications.fulfilled, (state, action) => {
        state.myApplications = action.payload
        state.myApplicationsLoading = false
      })
      .addCase(fetchMyApplications.rejected, (state, action) => {
        state.myApplicationsLoading = false
        toast.error(action.payload as string)
      })
      .addCase(withdrawApplication.fulfilled, (state, action) => {
        const index = state.myApplications.findIndex((application) => application.id === action.payload.id)
        if (index !== -1) state.myApplications[index] = action.payload
      })
      .addCase(fetchApplicationDetail.pending, (state) => {
        state.selectedApplicationLoading = true
      })
      .addCase(fetchApplicationDetail.fulfilled, (state, action) => {
        state.selectedApplication = action.payload
        state.selectedApplicationLoading = false
      })
      .addCase(fetchApplicationDetail.rejected, (state, action) => {
        state.selectedApplicationLoading = false
        state.selectedApplication = null
        toast.error(action.payload as string)
      })
      .addCase(acceptOffer.fulfilled, (state) => {
        if (state.selectedApplication) state.selectedApplication.status = 'OFFER_ACCEPTED'
      })
      .addCase(declineOffer.fulfilled, (state) => {
        if (state.selectedApplication) state.selectedApplication.status = 'OFFER_DECLINED'
      })
  },
  initialState,
  name: 'studentJobs',
  reducers: {
    clearSelectedJob: (state) => {
      state.selectedJob = null
    },
  },
})

export const { clearSelectedJob } = studentJobsSlice.actions

interface RootStateSlice {
  studentJobs: StudentJobsState
}

export const selectStudentJobs = (state: RootStateSlice): BackendJob[] => state.studentJobs.jobs
export const selectStudentJobsLoading = (state: RootStateSlice): boolean => state.studentJobs.jobsLoading
export const selectStudentJobsPage = (state: RootStateSlice): number => state.studentJobs.jobsPage
export const selectStudentJobsPages = (state: RootStateSlice): number => state.studentJobs.jobsPages
export const selectSelectedJob = (state: RootStateSlice): BackendJob | null => state.studentJobs.selectedJob
export const selectSelectedJobLoading = (state: RootStateSlice): boolean => state.studentJobs.selectedJobLoading
export const selectSavedJobIds = (state: RootStateSlice): string[] => state.studentJobs.savedJobIds
export const selectRecommendedJobs = (state: RootStateSlice): RecommendedJob[] => state.studentJobs.recommendedJobs
export const selectRecommendedJobsLoading = (state: RootStateSlice): boolean => state.studentJobs.recommendedJobsLoading
export const selectIsApplyingToJob = (state: RootStateSlice, jobId: string): boolean => state.studentJobs.applyingJobIds.includes(jobId)
export const selectMyApplications = (state: RootStateSlice): BackendApplication[] => state.studentJobs.myApplications
export const selectMyApplicationsLoading = (state: RootStateSlice): boolean => state.studentJobs.myApplicationsLoading
export const selectHasAppliedToJob = (state: RootStateSlice, jobId: string): boolean =>
  state.studentJobs.myApplications.some((application) => application.jobId === jobId && application.status !== 'WITHDRAWN')
export const selectSelectedApplication = (state: RootStateSlice): BackendApplication | null => state.studentJobs.selectedApplication
export const selectSelectedApplicationLoading = (state: RootStateSlice): boolean => state.studentJobs.selectedApplicationLoading

export default studentJobsSlice.reducer
